/**
 * CallSounds — Centralized Web Audio API sound manager.
 * Only one sound plays at a time. Ring loops until explicitly stopped.
 */

const callSounds = {
  _currentTimeout: null,
  _ringIntervalId: null,

  _stopCurrent() {
    if (this._currentTimeout) {
      clearTimeout(this._currentTimeout);
      this._currentTimeout = null;
    }
    if (this._ringIntervalId) {
      clearInterval(this._ringIntervalId);
      this._ringIntervalId = null;
    }
  },

  _getContext() {
    if (!this._audioCtx) {
      this._audioCtx = new AudioContext();
    }
    if (this._audioCtx.state === 'suspended') {
      this._audioCtx.resume();
    }
    return this._audioCtx;
  },

  _playTone(freq, duration, type = 'sine', volume = 0.25) {
    try {
      const ctx = this._getContext();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = type;
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (err) {
      console.warn('[CallSounds] _playTone failed:', err);
    }
  },

  /** Stop any currently playing sound */
  stop() {
    this._stopCurrent();
  },

  /** Incoming call ring — loops every 1.5s until stopped */
  playRing() {
    this._stopCurrent();
    const ringOnce = () => {
      this._playTone(440, 0.12, 'sine', 0.2);
      setTimeout(() => this._playTone(440, 0.12, 'sine', 0.2), 220);
      setTimeout(() => this._playTone(523, 0.12, 'sine', 0.2), 440);
    };
    ringOnce();
    this._ringIntervalId = setInterval(ringOnce, 1500);
    // Auto-stop ring after 30s (caller might not answer)
    this._currentTimeout = setTimeout(() => this._stopCurrent(), 30000);
  },

  /** Call connected — ascending pleasant tone */
  playConnect() {
    this._stopCurrent();
    this._playTone(523, 0.1, 'sine', 0.18);
    setTimeout(() => this._playTone(659, 0.12, 'sine', 0.18), 90);
    setTimeout(() => this._playTone(784, 0.15, 'sine', 0.18), 210);
  },

  /** Call ended — descending tone */
  playEnd() {
    this._stopCurrent();
    this._playTone(392, 0.15, 'triangle', 0.12);
    setTimeout(() => this._playTone(330, 0.15, 'triangle', 0.1), 120);
    setTimeout(() => this._playTone(262, 0.2, 'triangle', 0.08), 260);
  },

  /** Call missed / rejected — short buzz */
  playMissed() {
    this._stopCurrent();
    this._playTone(300, 0.1, 'square', 0.08);
    setTimeout(() => this._playTone(250, 0.15, 'square', 0.06), 100);
  },
};

export default callSounds;
